import type {
  ActivityReply,
  AgeGateReply,
  AlbumGrantRow,
  AlbumRow,
  AnnouncementsReply,
  AnonymousSignupBody,
  BodyType,
  BugReportBody,
  ChainLeadersReply,
  ChainReply,
  CheckinBody,
  CheckinReply,
  ClearCalledOffReply,
  ConversationDetail,
  ConversationRow,
  ConversationSearchHit,
  DeviceRow,
  EarnReply,
  EntryOptions,
  EntryWhereReply,
  EventAudience,
  EventAudienceReply,
  EventCreateBody,
  EventDetail,
  EventGuestsReply,
  EventInvitedReply,
  EventMeetAnswer,
  EventMeetFixBody,
  EventMeetGoBody,
  EventMeetInfo,
  EventMeetReply,
  EventMeetSendBody,
  EventMeetShow,
  EventPatchBody,
  EventScope,
  ExportStatus,
  FavoritePatch,
  FavoriteRow,
  FlagBody,
  GateCountReply,
  GatePeopleReply,
  GatePeopleStage,
  GateReply,
  GridSort,
  GroupCreateBody,
  GroupDetail,
  GroupMembersReply,
  GroupPatchBody,
  HandoffReply,
  Health,
  InboxCounts,
  InviteCreateBody,
  InviteCreateReply,
  InvitePeek,
  InvitesReply,
  LegalDoc,
  LegalDocReply,
  LocationReply,
  MapLayer,
  MapPeople,
  MapReply,
  Me,
  MeetAnswerBody,
  MeetBlob,
  MeetBlobRow,
  MeetCallOffBody,
  MeetDidTheyShowBody,
  MeetFriendBody,
  MeetFriendCheckInBody,
  MeetFriendPage,
  MeetOnMyWayBody,
  MeetPlan,
  MeetRecordRow,
  MeetRow,
  MeetSpoofFlag,
  MentionCandidate,
  MessageRow,
  MyAppeal,
  MyEventRow,
  MyGroupRow,
  MyProfile,
  MyReport,
  MyStatusReply,
  NearbySpotsReply,
  NearMapReply,
  NearSpotsReply,
  NoteDetailsBody,
  NoteMarks,
  NoteReply,
  NoteRow,
  NoteTagsReply,
  NotificationSettings,
  OnboardingStep,
  PeopleReply,
  PersonCard,
  PersonNote,
  PersonSummary,
  PhotoOrder,
  PhotoPatch,
  PhotoRow,
  PlusGiveBody,
  PlusGiveReply,
  Position,
  ProfilePatch,
  PublicProfile,
  PushPrefs,
  QuickRepliesReply,
  ReactionTally,
  RecentsReply,
  RedeemInviteBody,
  ReportBody,
  SendMessageBody,
  SharedInvite,
  SharedInviteCreateBody,
  SocialGrantRow,
  SocialsPatch,
  SpotCreateBody,
  SpotCreateReply,
  SpotDetail,
  SpotFavoriteRow,
  SpotPatchBody,
  SpotSearchReply,
  SpotWallReply,
  StandingReply,
  StatusSetBody,
  TapBody,
  TapKind,
  TapRow,
  TapsReply,
  TestimonialBody,
  TestimonialDraft,
  TestimonialRow,
  TipRow,
  UploadCreateBody,
  UploadCreateReply,
  VetClipBody,
  VetClipDoneReply,
  VetClipReply,
  VetOutcomeBody,
  VetReportBody,
  VetReview,
  VetStartReply,
  ViewersReply,
  Visibility,
  VisibilityPatch,
  WallReply,
  WallSavedItem,
  YourPeopleReply,
} from '@feral/shared/client'
import { arrivalSource } from './source.js'

/** Stable per-browser device id (architecture §5.4). Random, never derived from anything personal. */
function deviceId(): string {
  try {
    let id = localStorage.getItem('fml.device')
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem('fml.device', id)
    }
    return id
  } catch {
    return 'ephemeral'
  }
}
const standalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (navigator as { standalone?: boolean }).standalone === true

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public retryAfter?: number,
  ) {
    super(message)
  }
}

/**
 * A 401 on anything but sign-in and /me itself: the session may have expired mid-use. session.ts
 * listens, checks /me once, and signs the device out if that's a 401 too (bugs.md: an expired session
 * showed "Can't reach the server" instead of the front door).
 */
let unauthorized: (() => void) | null = null
/** A request that didn't go through, for a bug report's notes (lib/diag.ts listens). */
let failed: ((method: string, path: string, status: number, message: string) => void) | null = null
export function whenFailed(fn: typeof failed): void {
  failed = fn
}
export function whenUnauthorized(fn: () => void): void {
  unauthorized = fn
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  // Outlives a page the tap leaves for (a server page, What's new), so the record still lands.
  opts: { keepalive?: boolean } = {},
): Promise<T> {
  const res = await fetch(`/v1${path}`, {
    method,
    keepalive: opts.keepalive,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      'x-device-id': deviceId(),
      'x-device-fp': `${navigator.platform}|${screen.width}x${screen.height}|${Intl.DateTimeFormat().resolvedOptions().timeZone}|${navigator.language}`,
      ...(standalone() ? { 'x-display-mode': 'standalone' } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  }).catch((err: unknown) => {
    failed?.(method, path, 0, `No answer: ${err instanceof Error ? err.message : String(err)}`)
    throw err
  })
  const text = await res.text()
  const json = text ? JSON.parse(text) : null
  if (!res.ok) {
    if (res.status === 401 && path !== '/me' && !path.startsWith('/auth/')) unauthorized?.()
    const e = json?.error ?? {}
    // Kept for a bug report (lib/diag.ts): what failed lately is often the whole story.
    failed?.(method, path, res.status, e.message ?? res.statusText)
    throw new ApiError(res.status, e.code ?? 'error', e.message ?? 'Something broke.', e.retryAfter)
  }
  return json as T
}

/** The Plus filters as the map and the Wall send them (FR-MAP-06, FR-WAL-14). */
export interface PlusFilterValues {
  active?: boolean
  photo?: boolean
  positions?: Position[]
  bodies?: BodyType[]
  ageMin?: number | null
  ageMax?: number | null
  maxKm?: number | null
  meet?: ('host' | 'travel')[]
}
function plusParams(p: URLSearchParams, f: PlusFilterValues) {
  if (f.active) p.set('active', 'true')
  if (f.photo) p.set('photo', 'true')
  if (f.positions?.length) p.set('positions', f.positions.join(','))
  if (f.bodies?.length) p.set('bodies', f.bodies.join(','))
  if (f.ageMin != null) p.set('ageMin', String(f.ageMin))
  if (f.ageMax != null) p.set('ageMax', String(f.ageMax))
  if (f.maxKm != null) p.set('maxKm', String(f.maxKm))
  if (f.meet?.length) p.set('meet', f.meet.join(','))
}

export const api = {
  // The time zone tells the server which country sign-up is from (decision 78: the US and Canada only).
  ageGate: (dob: string) =>
    call<AgeGateReply>('POST', '/age-gate', {
      dob,
      attest: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  /**
   * A sign-up screen opened before there's an account (GR-68), or the front door (GR-70), with this
   * visit's `?src=` tag so a public page's way in can be followed: counted only, so failures don't matter.
   */
  signupSeen: (step: 'enter' | 'location' | 'age' | 'account') =>
    call<{ ok: true }>('POST', '/entry/seen', { step, src: arrivalSource() }).catch(() => undefined),
  changeHandle: (handle: string) => call<{ ok: true; handle: string }>('POST', '/me/handle', { handle }),
  checkHandle: (handle: string) =>
    call<{ ok: boolean; reason?: string }>('GET', `/handles/check?handle=${encodeURIComponent(handle)}`),
  entryOptions: () => call<EntryOptions>('GET', '/entry/options'),
  entryWhere: (b: { lat: number; lng: number }) => call<EntryWhereReply>('POST', '/entry/where', b),
  anonymousSignup: (b: AnonymousSignupBody) =>
    call<{ id: string; handle: string; state: string }>('POST', '/accounts/anonymous', b),
  /** `e`: the proof an emailed link carries, which returns the address it went to */
  peekInvite: (code: string, e?: string | null) =>
    call<InvitePeek>(
      'GET',
      `/invites/peek?code=${encodeURIComponent(code)}${e ? `&e=${encodeURIComponent(e)}` : ''}`,
    ),
  redeemInvite: (b: RedeemInviteBody) =>
    call<{ id: string; handle: string; state: string; converted: boolean }>('POST', '/invites/redeem', b),
  me: () => call<Me>('GET', '/me'),
  /** The numbers Learn articles quote, as the app uses them now (public). */
  learnValues: () => call<Record<string, number>>('GET', '/learn/values'),
  /** The bell (decision 63): announcements for you, and how many you haven't opened. */
  announcements: () => call<AnnouncementsReply>('GET', '/announcements'),
  readAnnouncements: (ids: string[]) => call<{ ok: true }>('POST', '/announcements/read', { ids }),
  /** "Read more" tapped: the owner sees who opened it (console › Announcements). */
  openAnnouncement: (id: string) =>
    call<{ ok: true }>('POST', `/announcements/${id}/open`, undefined, { keepalive: true }),
  acceptRules: (version: number) => call<{ ok: true }>('POST', '/me/rules-accept', { version }),
  legal: (doc: LegalDoc) => call<LegalDocReply>('GET', `/legal/${doc}`),
  acceptLegal: (doc: LegalDoc, version: number) =>
    call<{ ok: true }>('POST', '/me/legal-accept', { doc, version }),
  sendMagicLink: (email: string) => call<{ ok: true }>('POST', '/auth/email/send', { email }),
  sendEmailCode: (email: string) => call<{ ok: true }>('POST', '/auth/email/claim', { email }),
  setEmailPrefs: (b: { news?: boolean; promos?: boolean; events?: boolean; reminders?: boolean }) =>
    call<{ news: boolean; promos: boolean; events: boolean; reminders: boolean }>(
      'PUT',
      '/me/email-prefs',
      b,
    ),
  setEmail: (email: string, code: string) =>
    call<{ ok: true; email: string }>('POST', '/me/email', { email, code }),
  verifyMagicLink: (token: string) =>
    call<{ id: string; handle: string; state: string }>('POST', '/auth/email/verify', { token }),
  /** `quick`: a Quick Exit, after which no reminder email may follow */
  logout: (quick = false) => call<{ ok: true }>('POST', '/auth/logout', quick ? { quick } : undefined),
  passkeyRegisterOptions: () => call<unknown>('POST', '/auth/passkey/register/options'),
  passkeyRegisterVerify: (r: unknown) => call<{ ok: true }>('POST', '/auth/passkey/register/verify', r),
  passkeyLoginOptions: () => call<unknown>('POST', '/auth/passkey/login/options'),
  passkeyLoginVerify: (r: unknown) =>
    call<{ id: string; handle: string; state: string }>('POST', '/auth/passkey/login/verify', r),
  markInstalled: () => call<{ ok: true }>('POST', '/me/installed'),
  devMagicLink: (email: string) =>
    call<{ url: string | null; code: string | null }>(
      'GET',
      `/dev/magic-link?email=${encodeURIComponent(email)}`,
    ),
  // Week 3: location and the map
  reportLocation: (lat: number, lng: number, manual: boolean) =>
    call<LocationReply>('PUT', '/me/location', manual ? { lat, lng, manual: true } : { lat, lng }),
  myLocation: () => call<LocationReply | null>('GET', '/me/location'),
  clearLocation: () => call<{ ok: true }>('DELETE', '/me/location'),
  map: (
    q: {
      bbox: [number, number, number, number]
      zoom: number
      layers: MapLayer[]
      online?: boolean
      hosting?: boolean
      looking?: boolean
      atSpot?: boolean
      list?: boolean
      people?: MapPeople
      viewers?: boolean
      fresh?: boolean
    } & PlusFilterValues,
  ) => {
    const p = new URLSearchParams({
      bbox: q.bbox.map((n) => n.toFixed(5)).join(','),
      zoom: String(Math.round(q.zoom * 10) / 10),
      layers: q.layers.join(','),
    })
    if (q.online) p.set('online', 'true')
    if (q.hosting) p.set('hosting', 'true')
    if (q.looking) p.set('looking', 'true')
    if (q.atSpot) p.set('atSpot', 'true')
    if (q.list) p.set('list', 'true')
    if (q.people && q.people !== 'all') p.set('people', q.people)
    if (q.viewers) p.set('viewers', 'true')
    if (q.fresh) p.set('fresh', 'true')
    plusParams(p, q)
    return call<MapReply>('GET', `/map?${p}`)
  },
  devPopulate: (lat: number, lng: number, n = 60) =>
    call<{ created: number }>('POST', '/dev/populate', { lat, lng, n }),
  devDepopulate: () => call<{ removed: number }>('DELETE', '/dev/populate'),
  // Week 4: profiles, photos, albums
  profile: () => call<MyProfile>('GET', '/me/profile'),
  patchProfile: (b: ProfilePatch) => call<MyProfile>('PATCH', '/me/profile', b),
  /** Testimonials (decision 187): the owner's quote of a member, and the member's say on it */
  testimonialDraft: (messageId: string) => call<TestimonialDraft>('GET', `/testimonials/draft/${messageId}`),
  askTestimonial: (b: TestimonialBody) => call<TestimonialRow>('POST', '/testimonials', b),
  testimonial: (id: string) => call<TestimonialRow>('GET', `/testimonials/${id}`),
  decideTestimonial: (id: string, to: 'allow' | 'decline' | 'withdraw') =>
    call<TestimonialRow>('POST', `/testimonials/${id}/${to}`),
  /** Partnered (decision 186): yes to someone who named you, or not (any more) */
  confirmPartner: (id: string) => call<MyProfile>('POST', `/me/partner-of/${id}/confirm`),
  unlinkPartner: (id: string) => call<MyProfile>('DELETE', `/me/partner-of/${id}`),
  patchVisibility: (b: VisibilityPatch) =>
    call<{ visibility: Visibility; ghost: boolean }>('PATCH', '/me/visibility', b),
  patchHealth: (h: Health) => call<Health>('PATCH', '/me/health', h),
  /** Your socials (decision 162): Public or By grant, and each network's username as typed or pasted. */
  patchSocials: (b: SocialsPatch) => call<MyProfile>('PATCH', '/me/socials', b),
  socialsGrants: () => call<SocialGrantRow[]>('GET', '/me/socials/grants'),
  revokeSocialsGrant: (gid?: string) =>
    call<{ revoked: number }>('DELETE', gid ? `/me/socials/grants/${gid}` : '/me/socials/grants'),
  patchPhoto: (id: string, b: PhotoPatch) => call<PhotoRow>('PATCH', `/me/photos/${id}`, b),
  deletePhoto: (id: string) => call<{ ok: true }>('DELETE', `/me/photos/${id}`),
  orderPhotos: (b: PhotoOrder) => call<{ ok: true }>('PUT', '/me/photos/order', b),
  createAlbum: (name: string) => call<AlbumRow>('POST', '/me/albums', { name }),
  renameAlbum: (id: string, name: string) => call<{ ok: true }>('PATCH', `/me/albums/${id}`, { name }),
  deleteAlbum: (id: string) => call<{ ok: true }>('DELETE', `/me/albums/${id}`),
  albumGrants: (id: string) => call<AlbumGrantRow[]>('GET', `/me/albums/${id}/grants`),
  revokeGrant: (id: string, gid?: string) =>
    call<{ revoked: number }>('DELETE', gid ? `/me/albums/${id}/grants/${gid}` : `/me/albums/${id}/grants`),
  createUpload: (b: UploadCreateBody) => call<UploadCreateReply>('POST', '/uploads', b),
  completeUpload: (id: string) => call<PhotoRow>('POST', `/uploads/${id}/complete`),
  completeSpotUpload: (id: string) => call<{ id: string; state: string }>('POST', `/uploads/${id}/complete`),
  deleteSpotPhoto: (spotId: string, photoId: string) =>
    call<{ ok: true }>('DELETE', `/spots/${spotId}/photos/${photoId}`),
  user: (id: string) => call<PublicProfile>('GET', `/users/${id}`),
  /** An @mention's card (decision 204): their summary for you, asked for when it's hovered. */
  personCard: (id: string) => call<PersonCard>('GET', `/users/${id}/card`),
  favorite: (id: string) => call<{ ok: true }>('PUT', `/me/favorites/${id}`),
  unfavorite: (id: string) => call<{ ok: true }>('DELETE', `/me/favorites/${id}`),
  favorites: () => call<FavoriteRow[]>('GET', '/me/favorites'),
  patchFavorite: (id: string, b: FavoritePatch) => call<{ ok: true }>('PATCH', `/me/favorites/${id}`, b),
  /** Your own order of the people you starred (Custom), first to last. */
  orderFavorites: (ids: string[]) => call<{ ok: true }>('PUT', '/me/favorites/order', { ids }),
  // Notes on people (FR-PRO-10, Plus); deleting is always allowed.
  notes: () => call<NoteRow[]>('GET', '/me/notes'),
  noteMarks: () => call<NoteMarks>('GET', '/me/notes/marks'),
  /** Your own tags across your notes, most used first (decision 180). */
  noteTags: () => call<NoteTagsReply>('GET', '/me/notes/tags'),
  // Quick replies (FR-CHT-12): your own list, or null for the console's defaults.
  quickReplies: () => call<QuickRepliesReply>('GET', '/me/quick-replies'),
  setQuickReplies: (replies: string[] | null) =>
    call<QuickRepliesReply>('PUT', '/me/quick-replies', { replies }),
  note: (id: string) => call<NoteReply>('GET', `/me/notes/${id}`),
  saveNote: (id: string, n: PersonNote) => call<NoteReply>('PUT', `/me/notes/${id}`, n),
  addNoteDetails: (id: string, details: NoteDetailsBody['details']) =>
    call<NoteReply & { added: number }>('POST', `/me/notes/${id}/details`, { details }),
  deleteNote: (id: string) => call<{ ok: true }>('DELETE', `/me/notes/${id}`),
  /** The note as a phone contact (a .vcf the browser hands to Contacts). */
  noteContactUrl: (id: string) => `/v1/me/notes/${id}/contact.vcf`,
  /** A spot as a phone contact: its name, phone and website. */
  spotContactUrl: (id: string) => `/v1/spots/${id}/contact.vcf`,
  /** Founders only (decision 48). */
  hasPlus: (userId: string) => call<{ plus: boolean }>('GET', `/users/${userId}/plus`),
  givePlus: (userId: string, length: PlusGiveBody['length']) =>
    call<PlusGiveReply>('POST', `/users/${userId}/plus`, { length }),
  block: (userId: string) => call<{ ok: true }>('POST', '/blocks', { userId }),
  unblock: (id: string) => call<{ ok: true }>('DELETE', `/blocks/${id}`),
  viewers: () => call<ViewersReply>('GET', '/me/viewers'),
  devices: () => call<DeviceRow[]>('GET', '/me/devices'),
  // Week 5: chat, taps, knocks, Recents
  conversations: () => call<ConversationRow[]>('GET', '/conversations'),
  /** your chats whose name or messages match (decision 185) */
  searchConversations: (q: string) =>
    call<ConversationSearchHit[]>('GET', `/conversations/search?q=${encodeURIComponent(q)}`),
  inboxCounts: () => call<InboxCounts>('GET', '/inbox/counts'),
  openConversation: (userId: string) => call<{ id: string }>('POST', '/conversations', { userId }),
  /** The newest page, or `before` an older message's time for the page ahead of it. */
  conversation: (id: string, page?: { since?: string; before?: string }) => {
    const q = new URLSearchParams(page as Record<string, string> | undefined).toString()
    return call<ConversationDetail>('GET', `/conversations/${id}${q ? `?${q}` : ''}`)
  },
  sendMessage: (id: string, b: SendMessageBody) =>
    call<MessageRow>('POST', `/conversations/${id}/messages`, b),
  /** `reactedAt`: the newest reaction the thread showed, which can be later than its newest message. */
  markRead: (id: string, upTo?: string, reactedAt?: string | null) =>
    call<{ ok: true }>('POST', `/conversations/${id}/read`, {
      ...(upTo ? { upTo } : {}),
      ...(reactedAt ? { reactedAt } : {}),
    }),
  /** FR-CHT-16: one emoji per person per message; a new one replaces yours. */
  react: (id: string, messageId: string, emoji: string) =>
    call<MessageRow>('PUT', `/conversations/${id}/messages/${messageId}/reaction`, { emoji }),
  unreact: (id: string, messageId: string) =>
    call<MessageRow>('DELETE', `/conversations/${id}/messages/${messageId}/reaction`),
  acceptConversation: (id: string) => call<{ ok: true }>('POST', `/conversations/${id}/accept`),
  // Meet (§5.17, decision 163)
  meets: (conversationId: string) => call<MeetRow[]>('GET', `/conversations/${conversationId}/meets`),
  myMeets: () => call<MeetRow[]>('GET', '/me/meets'),
  meet: (id: string) => call<MeetRow>('GET', `/meets/${id}`),
  proposeMeet: (conversationId: string, b: MeetPlan) =>
    call<MeetRow>('POST', `/conversations/${conversationId}/meets`, b),
  answerMeet: (id: string, b: MeetAnswerBody) => call<MeetRow>('POST', `/meets/${id}/answer`, b),
  changeMeet: (id: string, b: MeetPlan) => call<MeetRow>('POST', `/meets/${id}/change`, b),
  withdrawMeet: (id: string) => call<MeetRow>('POST', `/meets/${id}/withdraw`),
  stillOn: (id: string) => call<MeetRow>('POST', `/meets/${id}/still-on`),
  onMyWay: (id: string, b: MeetOnMyWayBody) => call<MeetRow>('POST', `/meets/${id}/on-my-way`, b),
  meetEta: (id: string, etaMin: number) => call<MeetRow>('POST', `/meets/${id}/eta`, { etaMin }),
  imHere: (id: string, near?: boolean) =>
    call<MeetRow>('POST', `/meets/${id}/here`, near === undefined ? {} : { near }),
  tellFriend: (id: string, b: MeetFriendBody) => call<{ token: string }>('POST', `/meets/${id}/friend`, b),
  updateFriend: (id: string, blob: MeetFriendBody['blob']) =>
    call<{ ok: true }>('PUT', `/meets/${id}/friend`, { blob }),
  friendCheckIn: (id: string, b: MeetFriendCheckInBody) =>
    call<MeetRow>('POST', `/meets/${id}/friend/check-in`, b),
  closeMeet: (id: string) => call<MeetRow>('POST', `/meets/${id}/close`),
  stopFriend: (id: string) => call<MeetRow>('DELETE', `/meets/${id}/friend`),
  friendPage: (token: string) => call<MeetFriendPage>('GET', `/friend/${encodeURIComponent(token)}`),
  meetSpoof: (id: string, flags: MeetSpoofFlag[]) => call<null>('POST', `/meets/${id}/spoof`, { flags }),
  meetPhoto: (id: string, answer: 'yes' | 'no' | 'skip') =>
    call<MeetRow>('POST', `/meets/${id}/photo`, { answer }),
  disputeMeet: (id: string, text: string) => call<MeetRow>('POST', `/meets/${id}/dispute`, { text }),
  together: (id: string, near: boolean) => call<MeetRow>('POST', `/meets/${id}/together`, { near }),
  meetCode: (id: string) => call<MeetRow>('POST', `/meets/${id}/code`),
  checkMeetCode: (id: string, code: string) => call<MeetRow>('POST', `/meets/${id}/code/check`, { code }),
  callOff: (id: string, b: MeetCallOffBody) => call<MeetRow>('POST', `/meets/${id}/call-off`, b),
  didTheyShow: (id: string, b: MeetDidTheyShowBody) => call<MeetRow>('POST', `/meets/${id}/did-they-show`, b),
  meetBlob: (id: string, b: MeetBlob) => call<null>('POST', `/meets/${id}/blob`, b),
  meetBlobs: (id: string) => call<MeetBlobRow[]>('GET', `/meets/${id}/blobs`),
  meetRecord: (userId: string) => call<MeetRecordRow>('GET', `/users/${userId}/meet-record`),
  muteConversation: (id: string) => call<{ muted: boolean }>('POST', `/conversations/${id}/mute`),
  pinConversation: (id: string, pinned: boolean) =>
    call<{ pinned: boolean }>('PUT', `/conversations/${id}/pinned`, { pinned }),
  deleteConversation: (id: string) => call<{ ok: true }>('DELETE', `/conversations/${id}`),
  clearCalledOff: () => call<ClearCalledOffReply>('POST', '/conversations/clear-called-off'),
  revokeAlbumInChat: (id: string, albumId: string) =>
    call<{ revoked: number }>('DELETE', `/conversations/${id}/albums/${albumId}`),
  /** Take back the full profile you showed in this thread (decision 131). */
  revokeProfileInChat: (id: string) => call<{ revoked: number }>('DELETE', `/conversations/${id}/profile`),
  /** Take back the socials you shared in this thread (decision 162). */
  revokeSocialsInChat: (id: string) => call<{ revoked: number }>('DELETE', `/conversations/${id}/socials`),
  tap: (b: TapBody) => call<{ id: string; kind: string }>('POST', '/taps', b),
  taps: () => call<TapsReply>('GET', '/taps'),
  knocks: () => call<TapRow[]>('GET', '/knocks'),
  dismissTap: (id: string) => call<{ ok: true }>('POST', `/taps/${id}/dismiss`),
  tapBack: (id: string, kind?: TapKind) =>
    call<{ conversationId: string; kind: TapKind }>('POST', `/taps/${id}/back`, kind ? { kind } : {}),
  recents: () => call<RecentsReply>('GET', '/me/recents'),
  deleteRecent: (id: string) => call<{ ok: true; was: string }>('DELETE', `/me/recents/${id}`),
  revokeDevice: (id: string) => call<{ ok: true }>('DELETE', `/me/devices/${id}`),
  // Week 6: invites, the Gate, the chain
  gate: () => call<GateReply>('GET', '/gate'),
  /** The Gate's Eligible and Anonymous tabs (decision 164), a page at a time. */
  gatePeople: (stage: GatePeopleStage, offset = 0) =>
    call<GatePeopleReply>('GET', `/gate/people?${new URLSearchParams({ stage, offset: String(offset) })}`),
  gatePing: () => call<{ pinged: number }>('POST', '/gate/ping'),
  gateCount: () => call<GateCountReply>('GET', '/gate/count'),
  /** Your people (decision 179): the people you vouched for, on their way to the Gate. */
  yourPeople: () => call<YourPeopleReply>('GET', '/me/people'),
  yourPeopleSeen: () => call<{ ok: true }>('POST', '/me/people/seen'),
  /** The list view's grid (FR-MAP-12): a page of everyone, nearest first, with the map's filters. */
  people: (
    f: {
      online?: boolean
      hosting?: boolean
      looking?: boolean
      atSpot?: boolean
      people?: MapPeople
      viewers?: boolean
      layers?: MapLayer[]
      sort?: GridSort
    } & PlusFilterValues,
    cursor?: string,
  ) => {
    const p = new URLSearchParams()
    if (f.sort && f.sort !== 'nearest') p.set('sort', f.sort)
    if (f.online) p.set('online', 'true')
    if (f.hosting) p.set('hosting', 'true')
    if (f.looking) p.set('looking', 'true')
    if (f.atSpot) p.set('atSpot', 'true')
    if (f.people && f.people !== 'all') p.set('people', f.people)
    if (f.viewers) p.set('viewers', 'true')
    if (f.layers?.length) p.set('layers', f.layers.join(','))
    plusParams(p, f)
    if (cursor) p.set('cursor', cursor)
    return call<PeopleReply>('GET', `/people?${p}`)
  },
  /** The Wall, with the Plus filters when set (FR-WAL-14); the server ignores them without Plus. */
  wall: (
    f: { online?: boolean; hosting?: boolean; looking?: boolean; atSpot?: boolean } & PlusFilterValues = {},
  ) => {
    const p = new URLSearchParams()
    if (f.online) p.set('online', 'true')
    if (f.hosting) p.set('hosting', 'true')
    if (f.looking) p.set('looking', 'true')
    if (f.atSpot) p.set('atSpot', 'true')
    plusParams(p, f)
    const qs = p.toString()
    return call<WallReply>('GET', `/wall${qs ? `?${qs}` : ''}`)
  },
  /**
   * `replyTo`: the post this one replies to, quoted above it (FR-WAL-16). `photo`: its one photo
   * (decision 178), a Recent or new upload, or one of your photos.
   */
  wallPost: (
    text: string,
    replyTo?: string,
    photo?: { mediaId?: string | undefined; photoId?: string | undefined },
  ) =>
    call<{ id: string }>('POST', '/wall', {
      text,
      ...(replyTo ? { replyTo } : {}),
      ...(photo?.mediaId ? { mediaId: photo.mediaId } : {}),
      ...(photo?.photoId ? { photoId: photo.photoId } : {}),
    }),
  /** The Wall's @ picker (decision 134): people near enough to read your post, nearest first. */
  wallMentionable: () => call<MentionCandidate[]>('GET', '/wall/mentionable'),
  /** A bug report's and a report's @ picker (decision 190): members by the handle typed. */
  mentionable: (q: string) => call<MentionCandidate[]>('GET', `/mentionable?q=${encodeURIComponent(q)}`),
  wallDelete: (id: string) => call<{ ok: true }>('DELETE', `/wall/${id}`),
  wallSave: (id: string) => call<{ ok: true }>('POST', `/wall/${id}/save`),
  wallUnsave: (id: string) => call<{ ok: true }>('DELETE', `/wall/${id}/save`),
  wallBlock: (id: string) => call<{ ok: true }>('POST', `/wall/${id}/block`),
  /** A post (FR-WAL-17): one emoji, replacing yours; unreact takes it off. */
  wallReact: (id: string, emoji: string) =>
    call<{ reactions: ReactionTally }>('PUT', `/wall/${id}/reaction`, { emoji }),
  wallUnreact: (id: string) => call<{ reactions: ReactionTally }>('DELETE', `/wall/${id}/reaction`),
  // A status or an On my way on the Wall (decision 150).
  wallRowReact: (type: 'status' | 'arriving', id: string, emoji: string) =>
    call<{ reactions: ReactionTally }>('PUT', `/wall/${type}/${id}/reaction`, { emoji }),
  wallRowUnreact: (type: 'status' | 'arriving', id: string) =>
    call<{ reactions: ReactionTally }>('DELETE', `/wall/${type}/${id}/reaction`),
  wallSaved: () => call<WallSavedItem[]>('GET', '/me/wall/saved'),
  // Spots you starred, and a spot's own Wall (decision 59).
  spotFavorite: (id: string) => call<{ ok: true }>('PUT', `/spots/${id}/favorite`),
  spotUnfavorite: (id: string) => call<{ ok: true }>('DELETE', `/spots/${id}/favorite`),
  spotFavorites: () => call<SpotFavoriteRow[]>('GET', '/me/spots/favorites'),
  orderSpotFavorites: (ids: string[]) => call<{ ok: true }>('PUT', '/me/spots/favorites/order', { ids }),
  spotWall: (id: string) => call<SpotWallReply>('GET', `/spots/${id}/wall`),
  // Week 10: activity (WF-SET-07) and notifications (WF-SET-02)
  activity: () => call<ActivityReply>('GET', '/me/activity'),
  /** Older activity, past `before` (the last row's createdAt), for infinite scroll. */
  activityBefore: (before: string) =>
    call<ActivityReply>('GET', `/me/activity?before=${encodeURIComponent(before)}`),
  emailCode: (email: string, code: string) =>
    call<{ id: string }>('POST', '/auth/email/code', { email, code }),
  handoff: () => call<HandoffReply>('POST', '/auth/handoff'),
  redeemHandoff: (code: string) => call<{ id: string }>('POST', '/auth/handoff/redeem', { code }),
  exportStatus: () => call<ExportStatus>('GET', '/me/export'),
  requestExport: () => call<ExportStatus>('POST', '/me/export'),
  deleteAccount: () => call<{ ok: true }>('POST', '/me/delete', { confirm: 'DELETE' }),
  activityRead: () => call<{ ok: true }>('POST', '/me/activity/read'),
  /** A row tapped; kept only for the owner's notifications (console › Notifications: who opened it). */
  activityOpen: (id: string) =>
    call<{ ok: true }>('POST', `/me/activity/${id}/open`, undefined, { keepalive: true }),
  activityClear: () => call<{ ok: true }>('DELETE', '/me/activity'),
  notifications: () => call<NotificationSettings>('GET', '/me/notifications'),
  setNotifications: (prefs: PushPrefs) =>
    call<NotificationSettings>('PUT', '/me/notifications', {
      prefs,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  setPushSubscription: (subscription: PushSubscriptionJSON | null) =>
    call<NotificationSettings>('PUT', '/me/push-subscription', {
      subscription,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  emailInvite: (id: string, email: string) =>
    call<{ ok: true; sentTo: string; expiresAt: string }>('POST', `/invites/${id}/email`, { email }),
  setVetPreference: (preference: 'any' | 'gatekeepers') =>
    call<{ preference: 'any' | 'gatekeepers' }>('PUT', '/me/vet-preference', { preference }),
  invites: () => call<InvitesReply>('GET', '/invites'),
  createInvite: (b: InviteCreateBody) => call<InviteCreateReply>('POST', '/invites', b),
  revokeInvite: (id: string) => call<{ ok: true }>('DELETE', `/invites/${id}`),
  renameInvite: (id: string, label: string) =>
    call<{ ok: true; label: string }>('PATCH', `/invites/${id}`, { label }),
  createSharedInvite: (b: SharedInviteCreateBody) => call<SharedInvite>('POST', '/invites/shared', b),
  disableSharedInvite: (id: string) => call<{ ok: true }>('DELETE', `/invites/shared/${id}`),
  chain: () => call<ChainReply>('GET', '/me/chain'),
  chainLeaders: () => call<ChainLeadersReply>('GET', '/chain/leaders'),
  earn: () => call<EarnReply>('GET', '/me/earn'),
  // Week 7: gesture-clip vetting
  vetStart: (restart = false) => call<VetStartReply>('POST', `/vets${restart ? '?restart=true' : ''}`),
  vetClip: (id: string, b: VetClipBody) => call<VetClipReply>('POST', `/vets/${id}/clips`, b),
  vetClipDone: (id: string, n: number) => call<VetClipDoneReply>('POST', `/vets/${id}/clips/${n}/complete`),
  vetSubmit: (id: string) => call<{ id: string; state: string }>('POST', `/vets/${id}/submit`),
  vetOpen: (id: string) => call<VetReview>('POST', `/vets/${id}/open`),
  vetReview: (id: string) => call<VetReview>('GET', `/vets/${id}/review`),
  vetOutcome: (id: string, b: VetOutcomeBody) =>
    call<{ id: string; outcome: string }>('POST', `/vets/${id}/outcome`, b),
  vetReport: (id: string, b: VetReportBody) =>
    call<{ ok: true; held: boolean }>('POST', `/vets/${id}/report`, b),
  welcomeSeen: () => call<{ ok: true }>('POST', '/gate/welcome/seen'),
  tourSeen: () => call<{ ok: true }>('POST', '/me/tour/seen'),
  /** Vouch for me on or off (decision 197): someone Anonymous asking vetted members nearby for a vouch. */
  vouchAsk: (on: boolean) => call<{ vouchAsk: Me['vouchAsk'] }>('PUT', '/me/vouch-ask', { on }),
  onboarding: (step: OnboardingStep | 'done') =>
    call<{ onboarding: OnboardingStep | null }>('POST', '/me/onboarding', { step }),
  // Week 8: statuses, spots, check-ins, tips, flags
  myStatus: () => call<MyStatusReply>('GET', '/me/status'),
  setStatus: (b: StatusSetBody) => call<MyStatusReply>('PUT', '/me/status', b),
  extendStatus: () => call<MyStatusReply>('POST', '/me/status/extend'),
  endStatus: () => call<MyStatusReply>('DELETE', '/me/status'),
  spot: (id: string) => call<SpotDetail>('GET', `/spots/${id}`),
  spotsNearby: () => call<NearbySpotsReply>('GET', '/spots/nearby'),
  // The grid's events and groups near your pin, nearest first (decision 97), for the layers that are on.
  mapNear: (layers: string[]) =>
    call<NearMapReply>('GET', `/map/near?layers=${encodeURIComponent(layers.join(','))}`),
  // The list view's spots, nearest first, a page at a time (cursor from the page before).
  spotsNear: (
    f: { favorites?: boolean; here?: boolean; active?: boolean; maxKm?: number | null; sort?: GridSort },
    cursor?: string,
  ) => {
    const p = new URLSearchParams()
    if (cursor) p.set('cursor', cursor)
    if (f.sort && f.sort !== 'nearest') p.set('sort', f.sort)
    if (f.favorites) p.set('favorites', '1')
    if (f.here) p.set('here', '1')
    if (f.active) p.set('active', '1')
    if (f.maxKm) p.set('maxKm', String(f.maxKm))
    const qs = p.toString()
    return call<NearSpotsReply>('GET', `/spots/near${qs ? `?${qs}` : ''}`)
  },
  searchSpots: (q: string) => call<SpotSearchReply>('GET', `/spots/search?q=${encodeURIComponent(q)}`),
  createSpot: (b: SpotCreateBody) => call<SpotCreateReply>('POST', '/spots', b),
  patchSpot: (id: string, b: SpotPatchBody) =>
    call<{ applied: boolean; spot: SpotDetail }>('PATCH', `/spots/${id}`, b),
  checkIn: (id: string, b: CheckinBody) => call<CheckinReply>('POST', `/spots/${id}/checkin`, b),
  extendCheckin: (id: string) => call<MyStatusReply>('POST', `/spots/${id}/checkin/extend`),
  leaveSpot: (id: string) => call<MyStatusReply>('DELETE', `/spots/${id}/checkin`),
  arrive: (id: string, minutes: number, visible: boolean) =>
    call<SpotDetail>('POST', `/spots/${id}/arrive`, { minutes, visible }),
  cancelArrival: (id: string) => call<SpotDetail>('DELETE', `/spots/${id}/arrive`),
  tips: (id: string) => call<{ tips: TipRow[] }>('GET', `/spots/${id}/tips`),
  addTip: (id: string, text: string) => call<{ tip: TipRow }>('POST', `/spots/${id}/tips`, { text }),
  deleteTip: (id: string, tipId: string) => call<{ ok: true }>('DELETE', `/spots/${id}/tips/${tipId}`),
  flagSpot: (id: string, b: FlagBody) => call<SpotDetail>('POST', `/spots/${id}/flags`, b),
  confirmFlag: (id: string, flagId: string) =>
    call<SpotDetail>('POST', `/spots/${id}/flags/${flagId}/confirm`),
  // Events (FR-EVT-*, decision 91)
  event: (id: string) => call<EventDetail>('GET', `/events/${id}`),
  myEvents: () => call<MyEventRow[]>('GET', '/me/events'),
  createEvent: (b: EventCreateBody) => call<EventDetail>('POST', '/events', b),
  patchEvent: (id: string, b: EventPatchBody) => call<EventDetail>('PATCH', `/events/${id}`, b),
  /** A repeating event's `following` or `all` calls off the series from it, or all of it (decision 158). */
  cancelEvent: (id: string, scope: EventScope = 'one') =>
    call<{ ok: true }>('DELETE', `/events/${id}${scope === 'one' ? '' : `?scope=${scope}`}`),
  joinEvent: (id: string) => call<EventDetail>('POST', `/events/${id}/join`),
  leaveEvent: (id: string) => call<EventDetail>('DELETE', `/events/${id}/join`),
  eventGuests: (id: string) => call<EventGuestsReply>('GET', `/events/${id}/guests`),
  /** The Invited tab (decision 173): invited and not answered yet, newest first, a page at a time. */
  eventInvited: (id: string, offset = 0) =>
    call<EventInvitedReply>('GET', `/events/${id}/invited?offset=${offset}`),
  letIn: (id: string, userId: string) => call<{ ok: true }>('POST', `/events/${id}/guests/${userId}/accept`),
  turnDown: (id: string, userId: string) =>
    call<{ ok: true }>('POST', `/events/${id}/guests/${userId}/decline`),
  invitable: (id: string) => call<{ people: PersonSummary[] }>('GET', `/events/${id}/invitable`),
  /** A moderator's bulk invite (decision 160): how many it would reach in the region, then send it. */
  eventAudience: (id: string, groups: EventAudience[]) =>
    call<EventAudienceReply>('GET', `/events/${id}/audience?groups=${groups.join(',')}`),
  bulkInvite: (id: string, audiences: EventAudience[]) =>
    call<{ count: number }>('POST', `/events/${id}/bulk-invite`, { audiences }),
  inviteToEvent: (id: string, userId: string) =>
    call<{ ok: true }>('POST', `/events/${id}/invite`, { userId }),
  // An event's Meet (decision 193): the host's requests and list, a guest's answer and way there.
  sendEventMeet: (id: string, b: EventMeetSendBody) => call<EventMeetInfo>('POST', `/events/${id}/meet`, b),
  eventMeet: (id: string) => call<EventMeetReply>('GET', `/events/${id}/meet`),
  eventMeetAnswer: (id: string, answer: EventMeetAnswer) =>
    call<EventMeetInfo>('POST', `/events/${id}/meet/answer`, { answer }),
  eventMeetGo: (id: string, b: EventMeetGoBody) => call<EventMeetInfo>('POST', `/events/${id}/meet/go`, b),
  eventMeetEta: (id: string, etaMin: number) =>
    call<EventMeetInfo>('POST', `/events/${id}/meet/eta`, { etaMin }),
  eventMeetFix: (id: string, b: EventMeetFixBody) => call<null>('POST', `/events/${id}/meet/fix`, b),
  eventMeetSharing: (id: string, on: boolean) =>
    call<EventMeetInfo>('POST', `/events/${id}/meet/sharing`, { on }),
  eventMeetHere: (id: string, near?: boolean) =>
    call<EventMeetInfo>('POST', `/events/${id}/meet/here`, near === undefined ? {} : { near }),
  eventMeetGuestHere: (id: string, userId: string) =>
    call<{ ok: true }>('POST', `/events/${id}/meet/guests/${userId}/here`),
  eventMeetPin: (id: string, userId: string, b: EventMeetFixBody) =>
    call<null>('POST', `/events/${id}/meet/guests/${userId}/pin`, b),
  eventMeetShow: (id: string, userId: string, answer: EventMeetShow) =>
    call<{ ok: true }>('POST', `/events/${id}/meet/guests/${userId}/show`, { answer }),
  // Groups (FR-GRP-*, decision 92)
  group: (id: string) => call<GroupDetail>('GET', `/groups/${id}`),
  myGroups: () => call<MyGroupRow[]>('GET', '/me/groups'),
  groupCity: (lat: number, lng: number) =>
    call<{ city: string | null }>('GET', `/groups/city?lat=${lat}&lng=${lng}`),
  createGroup: (b: GroupCreateBody) => call<GroupDetail>('POST', '/groups', b),
  patchGroup: (id: string, b: GroupPatchBody) => call<GroupDetail>('PATCH', `/groups/${id}`, b),
  closeGroup: (id: string) => call<{ ok: true }>('DELETE', `/groups/${id}`),
  joinGroup: (id: string) => call<GroupDetail>('POST', `/groups/${id}/join`),
  leaveGroup: (id: string) => call<{ ok: true }>('DELETE', `/groups/${id}/join`),
  groupShowOnProfile: (id: string, showOnProfile: boolean) =>
    call<GroupDetail>('PUT', `/groups/${id}/me`, { showOnProfile }),
  groupMembers: (id: string) => call<GroupMembersReply>('GET', `/groups/${id}/members`),
  groupAccept: (id: string, userId: string) =>
    call<{ ok: true }>('POST', `/groups/${id}/members/${userId}/accept`),
  groupDecline: (id: string, userId: string) =>
    call<{ ok: true }>('POST', `/groups/${id}/members/${userId}/decline`),
  groupMakeAdmin: (id: string, userId: string, admin: boolean) =>
    call<{ ok: true }>(admin ? 'POST' : 'DELETE', `/groups/${id}/members/${userId}/admin`),
  groupInvitable: (id: string) => call<{ people: PersonSummary[] }>('GET', `/groups/${id}/invitable`),
  inviteToGroup: (id: string, userId: string) =>
    call<{ ok: true }>('POST', `/groups/${id}/invite`, { userId }),
  groupEvents: (id: string) =>
    call<{ id: string; title: string; startsAt: string; endsAt: string; repeat: string | null }[]>(
      'GET',
      `/groups/${id}/events`,
    ),
  // A group's or event's chat, for whoever runs it (WF-GRP-05)
  pinMessage: (conversationId: string, messageId: string | null) =>
    call<{ ok: true }>('PUT', `/conversations/${conversationId}/pin`, { messageId }),
  /** Gone for everyone: your own with Plus (FR-CHT-17), or anyone's as a room's admin. */
  deleteMessage: (conversationId: string, messageId: string) =>
    call<{ ok: true }>('DELETE', `/conversations/${conversationId}/messages/${messageId}`),
  /** A group's or event's chat's @ picker (decision 134): the people in it; none in a 1:1 chat. */
  roomMentionable: (conversationId: string) =>
    call<MentionCandidate[]>('GET', `/conversations/${conversationId}/mentionable`),
  /** New words for one of your own text messages (FR-CHT-17, Plus); the reply is the message as it stands. */
  editMessage: (conversationId: string, messageId: string, body: string) =>
    call<MessageRow>('PATCH', `/conversations/${conversationId}/messages/${messageId}`, { body }),
  muteInRoom: (conversationId: string, userId: string, on: boolean) =>
    call<{ silencedUntil: string | null }>(
      on ? 'POST' : 'DELETE',
      `/conversations/${conversationId}/members/${userId}/mute`,
    ),
  removeFromRoom: (conversationId: string, userId: string) =>
    call<{ ok: true }>('POST', `/conversations/${conversationId}/members/${userId}/remove`),
  // Week 9: safety
  report: (b: ReportBody) => call<{ id: string; hidden: boolean; held: boolean }>('POST', '/reports', b),
  myReports: () => call<MyReport[]>('GET', '/me/reports'),
  standing: () => call<StandingReply>('GET', '/me/standing'),
  blocks: () => call<{ id: string; handle: string | null; createdAt: string }[]>('GET', '/blocks'),
  myAppeal: () => call<MyAppeal | null>('GET', '/me/appeal'),
  sendAppeal: (statement: string) => call<MyAppeal | null>('POST', '/me/appeal', { statement }),
  reportBug: (b: BugReportBody) => call<{ ok: true; id: string }>('POST', '/bugs', b),
}
export { standalone }
